import React, { useContext } from 'react';
import Chip from '../../../../../atomicDesign/atoms/Chips/Chip';
import Stack from '../../../../../atomicDesign/atoms/Stack';
import Text from '../../../../../atomicDesign/atoms/Text';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { fmt, monthName } from './utils';
import type { MonthRow } from './types';
import { useDataExporter } from '../../../../../customhooks/useExporter';
import IconOnlyButton from '../../../../../atomicDesign/molecules/Buttons/IconOnlyButton';
import AdminContext from '../../../../../context/admin';

import SimpleTable from '../../../../../atomicDesign/molecules/SimpleTable';
import DataRow from '../../../../../atomicDesign/atoms/DataRow';
import DataCell from '../../../../../atomicDesign/atoms/DataCell';
import MuiFaIcon from '../../../../../atomicDesign/atoms/MuiFaIcon';

type AdminContextType = {
  language?: {
    eosDashboard?: {
      noDataAvailable?: string;
      baselineChart?: {
        average?: string;
        total?: string;
        rightAxis?: string;
        co2Total?: string;
      };
    };
    settings?: {
      baseline?: {
        monthlySummary?: string;
        month?: string;
        energyType?: string;
        year?: string;
        energyTypes?: {
          [key: string]: string;
        };
      };
      areas?: {
        devices?: string;
      };
    };
  };
};

export default function MonthlySummarySection({
  monthRows,
  buildingName,
}: {
  monthRows: MonthRow[];
  buildingName: string | undefined;
}) {
  const { handleExport } = useDataExporter() as {
    handleExport: (
      type: 'excel' | 'csv',
      options: any,
      setFeedback?: any
    ) => void;
  };

  const exportMonthly = (type: 'excel' | 'csv') =>
    handleExport(type, {
      data: monthRows,
      filename: `baseline_monthly_${buildingName ?? 'building'}`,
      sheetName: 'Monthly',
      headers: [
        'year',
        'month',
        'typeOfEnergy',
        'avg',
        'total',
        'kWp',
        'co2Avg',
        'numberOfDevices',
        'excluded',
      ],
    });

  const { language } = useContext(AdminContext) as AdminContextType;

  return (
    <Accordion>
      <AccordionSummary
        expandIcon={<MuiFaIcon name="fa-solid fa-chevron-down" />}
        sx={{ typography: 'subtitle1' }}
      >
        <Stack flexDirection="row" sx={{ alignItems: 'center', gap: 2 }}>
          <Text>
            {language?.settings?.baseline?.monthlySummary ?? 'Monthly summary'}
          </Text>
          <IconOnlyButton
            icon={
              <MuiFaIcon
                name="fa-regular fa-download"
                style={{ fontSize: 14 }}
              />
            }
            onClick={() => exportMonthly('excel')}
            disabled={!monthRows.length}
          />
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <SimpleTable
          sx={{ mb: 0 }}
          columns={[
            { label: language?.settings?.baseline?.year ?? 'Year' },
            { label: language?.settings?.baseline?.month ?? 'Month' },
            { label: language?.settings?.baseline?.energyType ?? 'Energy' },
            {
              label: language?.eosDashboard?.baselineChart?.average ?? 'Avg',
              align: 'right',
            },
            {
              label: language?.eosDashboard?.baselineChart?.total ?? 'Total',
              align: 'right',
            },
            {
              label: language?.eosDashboard?.baselineChart?.rightAxis ?? 'kWp',
              align: 'right',
            },
            {
              label:
                language?.eosDashboard?.baselineChart?.co2Total ??
                'CO₂ total in kg',
              align: 'right',
            },
            {
              label: language?.settings?.areas?.devices ?? 'Number of Devices',
              align: 'right',
            },
          ]}
        >
          {monthRows.map((r) => (
            <DataRow
              key={r.id}
              sx={{
                opacity: r.excluded ? 0.45 : 1,
                '&:hover': { cursor: 'default' },
              }}
            >
              <DataCell>{r.year}</DataCell>
              <DataCell>{monthName(r.month)}</DataCell>
              <DataCell>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Text variant="body2">
                    {language?.settings?.baseline?.energyTypes?.[
                      r.typeOfEnergy
                    ] ?? r.typeOfEnergy}
                  </Text>
                  {r.excluded && <Chip size="small" label="Excluded" />}
                </Stack>
              </DataCell>
              <DataCell align="right">{fmt(r.avg)}</DataCell>
              <DataCell align="right">{fmt(r.total)}</DataCell>
              <DataCell align="right">{fmt(r.kWp)}</DataCell>
              <DataCell align="right">{fmt(r.totalCo2 / 1000)}</DataCell>
              <DataCell align="right">{r.breakdownCount}</DataCell>
            </DataRow>
          ))}

          {monthRows.length === 0 && (
            <DataRow>
              <DataCell colSpan={8}>
                <Text variant="body2">
                  {language?.eosDashboard?.noDataAvailable ?? 'No data.'}
                </Text>
              </DataCell>
            </DataRow>
          )}
        </SimpleTable>
      </AccordionDetails>
    </Accordion>
  );
}
